import { FSEventEmitter } from "../fotastart";
import { app, payload } from "modular-openscriptjs";
import { $e } from "../ojs.events";

export class EchoEventListener extends FSEventEmitter {
    listenedEvents = new Map();

    /**
     * Listen to an Echo event on a channel.
     * @param {string} channel - Channel name
     * @param {string} event - Event name
     * @param {string} visibility - 'public' or 'private'
     */
    listen(channel, event, visibility = "private") {
        if (typeof Echo === "undefined") {
            console.warn("Echo is not defined. Make sure to include Echo.js.");
            return;
        }

        const key = `${channel}:${event}`;
        if (this.listenedEvents.has(key)) {
            return;
        }

        this.listenedEvents.set(key, { channel, event, visibility });

        let c =
            visibility === "private"
                ? Echo.private(channel)
                : Echo.channel(channel);

        try {
            c.listen(event, (data) => {
                this.handle(event, data);
            });
        } catch (e) {
            console.error(
                `Error listening to Echo event "${event}" on channel "${channel}":`,
                e,
            );
        }
    }

    // Alias to support either listen or listenToEvent naming
    listenToEvent(channel, event, visibility = "private") {
        this.listen(channel, event, visibility);
    }

    /**
     * Listen to notifications on a channel.
     * Ensures idempotency to listen only once.
     * @param {string} channel
     * @param {string} visibility - 'public' or 'private'
     */
    listenToNotifications(channel, visibility = "private") {
        if (typeof Echo === "undefined") {
            console.warn("Echo is not defined. Make sure to include Echo.js.");
            return;
        }

        const key = `notifications:${channel}`;
        if (this.listenedEvents.has(key)) {
            return;
        }

        this.listenedEvents.set(key, {
            channel,
            type: "notification",
            visibility,
        });

        let c =
            visibility === "private"
                ? Echo.private(channel)
                : Echo.channel(channel);

        try {
            c.notification(this.notificationCallback);
        } catch (e) {
            console.error(
                `Error listening to Echo notifications on channel "${channel}":`,
                e,
            );
        }
    }

    /**
     * Handle incoming database notification data.
     * Dispatches via FSEventEmitter and optional openScript event broker.
     * @param {*} notification
     */
    handleNotification(notification) {
        this.emit("notification", notification);

        const broker = app("broker");
        if (broker && $e) {
            broker.send($e.system.has.notification, payload({ notification }));
        }
    }

    /**
     * Handle incoming Echo event data.
     * Dispatches via FSEventEmitter and optional openScript event broker.
     * @param {string} event
     * @param {*} data
     */
    handle(event, data) {
        this.emit(event, data);

        const broker = app("broker");

        if (broker && $e && $e.echo && $e.echo[event]) {
            broker.send(
                $e.echo[event],
                payload({ eventName: event, data: data }),
            );
        }
    }

    /**
     * Leave a channel completely.
     * @param {string} channel
     */
    leave(channel) {
        if (typeof Echo === "undefined") return;
        try {
            Echo.leave(channel);
            for (let [key, val] of this.listenedEvents.entries()) {
                if (val.channel === channel) {
                    this.listenedEvents.delete(key);
                }
            }
        } catch (e) {
            console.error(`Error leaving Echo channel "${channel}":`, e);
        }
    }

    /**
     * Stop listening to a specific event on a channel.
     * @param {string} channel
     * @param {string} event
     * @param {string} visibility
     */
    stopListening(channel, event, visibility = "private") {
        if (typeof Echo === "undefined") return;
        let c =
            visibility === "private"
                ? Echo.private(channel)
                : Echo.channel(channel);

        try {
            c.stopListening(event);
            const key = `${channel}:${event}`;
            this.listenedEvents.delete(key);
        } catch (e) {
            console.error(
                `Error stopping listening to event "${event}" on channel "${channel}":`,
                e,
            );
        }
    }

    /**
     * Stop listening to database notifications on a channel.
     * @param {string} channel
     * @param {string} visibility
     */
    stopListeningToNotifications(channel, visibility = "private") {
        if (typeof Echo === "undefined") return;
        let c =
            visibility === "private"
                ? Echo.private(channel)
                : Echo.channel(channel);

        try {
            c.stopListeningForNotification(this.notificationCallback);
            const key = `notifications:${channel}`;
            this.listenedEvents.delete(key);
        } catch (e) {
            console.error(
                `Error stopping listening to notifications on channel "${channel}":`,
                e,
            );
        }
    }

    notificationCallback(notification) {
        let handler = app("EchoEventListener");

        if (handler) {
            handler.handleNotification(notification);
        }
    }
}
